// GameTopBar.tsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Fonts from "../../../ui/global/Fonts";
import Button from "../../../ui/components/Button";
import TabPanel from "../../../ui/components/TabPanel";
import InputSimple from "../../../ui/components/InputSimple";
import ProfileButton from "../../website/ProfileButton";
import Tooltip from "../../../ui/components/Tooltip";
import Logo from "../../../ui/components/Logo";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DeviceType } from "./GamePreview";
import DebugGameArtButton from "./internal/DebugGameArtButton";
import { GameModes } from "../Game";
import { useMediaQuery } from "@mui/material";
import  GithubButton from "./integrations/GithubButton";
import { GithubFormat } from "../utils/formatCode";
import HomeLogoButton from "../../website/HomeLogoButton";
import { getTemplate } from '../../../utils/template';
import MenuOpen from "../../../ui/components/MenuOpen";
import { useGame } from "../../../backend/useGame";
import { useGameMetrics } from "../../../backend/useGameMetrics";
import { useUser } from "../../../backend/useUser";
import { captureEvent } from "../../../utils/analytics";
import { getWebsiteUrl } from "../../../utils/urls";

const Top = styled.div`
    position: fixed;
    top: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 54px;
    background-color: ${Colors.black};
    padding: 5px;
    margin: auto 0;
    gap: 10px;
    z-index: 100;
`;

const Left = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
    gap: 0px;
`;

const Middle = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex: 1;
    gap: 6px;
`;

const Right = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    flex: 1;
    gap: 6px;
`;

const Divider = styled.div`
    width: 1px;
    height: 24px;
    background-color: ${Colors.grey8};
    margin: 0 4px;
`;

const GameName = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 16px;
    line-height: 1.4em;
    color: ${Colors.white};
    text-wrap: nowrap;
    border-radius: 8px;
    margin: 0 10px;

    &:hover {
        background-color: ${Colors.grey9};
    }
`;

interface GameTopBarProps {
    config: any;
    gameId: string;
    gameName: string;
    mode: GameModes;
    setMode: (mode: 'Game' | 'Code' | 'Plan') => void;
    onHomeClick: () => void;
    onRefreshClick: () => void;
    handleGameNameChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onDeviceClick: () => void;
    devicePreview: DeviceType;
    incrementGameArtTriggerReload: () => void;
    gameCreateCompleted: boolean;
    latestUserFacingHistoryId: string;
    latestUserFacingCode: GithubFormat;
    latestUserFacingPrompt: string;
    latestUserFacingStatus: string;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
    currentGameHistoryId?: string;
    isViewingLatest?: boolean;
}

const GameTopBar: React.FC<GameTopBarProps> = ({
    config,
    gameId,
    gameName,
    mode,
    setMode,
    onHomeClick,
    onRefreshClick,
    handleGameNameChange,
    onDeviceClick,
    devicePreview,
    incrementGameArtTriggerReload,
    gameCreateCompleted,
    latestUserFacingHistoryId,
    latestUserFacingCode,
    latestUserFacingPrompt,
    latestUserFacingStatus,
    isGeneratingGame,
    isUpdatingGameArt,
    currentGameHistoryId,
    isViewingLatest,
}) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isPhaserReact = getTemplate(latestUserFacingCode || []) === 'phaser-react';
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [isRemixing, setIsRemixing] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUser();
    const { remixGame } = useGame();
    const { incrementRemixes } = useGameMetrics();

    const onShareClick = () => {
        const url = window.location.href;
        let finalUrl = url;

        if (config.REDIRECT_TO_WEBSITE) {
            const ending = (window.location.pathname + window.location.search).replace('game', 'play');
            finalUrl = getWebsiteUrl(ending);
        } else {
            finalUrl = url.replace('game', 'play');
        }
        // console.log('finalUrl', finalUrl);

        window.open(finalUrl, '_blank');
    };

    const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (menuAnchorEl) {
            setMenuAnchorEl(null);
        } else {
            setMenuAnchorEl(event.currentTarget);
        }
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const onRemixClick = async () => {
        handleMenuClose();
        if (!user) {
            const state = { from: location } as any;
            navigate('/login', { state });
            return;
        }
        try {
            setIsRemixing(true);
            const historyIdToRemix = !isViewingLatest && currentGameHistoryId ? currentGameHistoryId : undefined;
            // console.log('[GameTopBar] Remix Game clicked', {
            //     gameId,
            //     isViewingLatest,
            //     currentGameHistoryId,
            //     historyIdToRemix,
            // });
            const newGame = await remixGame(gameId, historyIdToRemix);
            captureEvent('remixed game', { original_game_id: gameId, new_game_id: newGame.id });
            // console.log('[GameTopBar] Remix completed', { newGameId: newGame.id });
            incrementRemixes(gameId);
            navigate(`/game/${newGame.id}`);
        } catch (error) {
            console.error('Error remixing game:', error);
        } finally {
            setIsRemixing(false);
        }
    };

    // useEffect(() => {
    //     console.log('[GameTopBar] data', {
    //         isViewingLatest,
    //         currentGameHistoryId,
    //     });
    // }, [isViewingLatest, currentGameHistoryId]);

    const menuOptions = [
        { text: 'Remix Game', icon: 'swap', onClick: onRemixClick, disabled: isRemixing },
    ];

    const tabs = [
        { name: 'Plan', icon: 'lightbulb', },
        { name: 'Game', icon: 'play', },
        { name: 'Code', icon: 'code', },
    ];

    return (
        <Top>
            <Left>
                <HomeLogoButton onHomeClick={onHomeClick} linkTo="/game" />
                <GameName>
                    <InputSimple
                        value={gameName}
                        colorMode='dark'
                        textAlign='left'
                        onChange={handleGameNameChange}
                        onEnterPress={() => {}}
                    />
                </GameName>
                <DebugGameArtButton
                    config={config}
                    gameId={gameId}
                    incrementGameArtTriggerReload={incrementGameArtTriggerReload}
                    gameCreateCompleted={gameCreateCompleted}
                />
            </Left>

            <Middle>
                {config.DEBUG_GAME_TABS && (
                    <TabPanel tabs={tabs}
                        onSelect={(mode: 'Game' | 'Code' | 'Plan' ) => setMode(mode)}
                        defaultTabName={mode}
                        colorMode={'dark'}
                        size={'small'}
                    />
                )}
            </Middle>

            <Right>
                <Tooltip text="Refresh" colorMode="dark">
                    <Button size="tiny" color="ghost white" icon="reset" onClick={onRefreshClick} />
                </Tooltip>
                {!isMobile && (
                    <Tooltip text={devicePreview === 'Mobile' ? 'Preview Computer' : 'Preview Mobile'} colorMode="dark">
                        <Button size="tiny" color="ghost white" icon={devicePreview === 'Mobile' ? 'computer' : 'mobile'} onClick={onDeviceClick} />
                    </Tooltip>
                )}
                {!isMobile && config.GITHUB_SYNC && isPhaserReact && (
                    <GithubButton 
                        config={config} 
                        gameName={gameName} 
                        gameId={gameId} 
                        historyId={latestUserFacingHistoryId}
                        localCode={latestUserFacingCode}
                        prompt={latestUserFacingPrompt}
                        historyStatus={latestUserFacingStatus}
                        isGeneratingGame={isGeneratingGame}
                        isUpdatingGameArt={isUpdatingGameArt}
                    />
                )}
                <Button size="tiny" color="black" rainbowShimmer={true} text="Share" icon="share" onClick={onShareClick} />
                {config.ENABLE_GAME_MORE_MENU ? (
                    <>
                        <Button size="tiny" color="ghost white" icon="more" onClick={handleMoreClick} />
                        <MenuOpen
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={handleMenuClose}
                            options={menuOptions}
                            minWidth={80}
                            color="black"
                            // placement="top-start"
                        />
                    </>
                ) : <Divider />}
                
                <ProfileButton config/>
            </Right>
        </Top>
    );
};

export default GameTopBar;